import { Box, Flex, VStack, HStack, Text, Button, Divider, useColorModeValue, Tooltip, Collapse, Image } from "@chakra-ui/react";
import logoSymbol from "@/assets/logo_transparent.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
	faHome, faFolderOpen, faFileAlt, faBullhorn, faCog, faCube, 
	faTowerBroadcast, faMobileScreen, faListAlt, faLifeRing, faPhotoFilm, 
	faQuestionCircle, faFileInvoiceDollar, faChartLine, faEnvelope, 
	faChartPie, faChevronDown, faChevronRight, faStore, faReceipt 
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { apiGetRequest } from "@/functions/requests";

export default function Sidebar() {
	const navigate = useNavigate();
	const bg = useColorModeValue("gray.50", "gray.900");
	const borderColor = useColorModeValue("rgba(226, 232, 240, 0.8)", "rgba(45, 55, 72, 0.8)");
	
	const { user, signOut } = useAuthenticator();
	const [userRole, setUserRole] = useState<string | null>(null);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [isSuspended, setIsSuspended] = useState(false);
	const location = useLocation();

	const isBroadcastRoute = location.pathname === '/broadcasts' || location.pathname === '/mobile-banner';
	const [isBroadcastExpanded, setIsBroadcastExpanded] = useState<boolean>(true);

	const isAccountsRoute = location.pathname.startsWith('/accounts') || location.pathname === '/finance';
	const [isAccountsExpanded, setIsAccountsExpanded] = useState<boolean>(true);

	useEffect(() => {
		if (isBroadcastRoute) {
			setIsBroadcastExpanded(true);
		}
	}, [location.pathname]);

	useEffect(() => {
		if (isAccountsRoute) {
			setIsAccountsExpanded(true);
		}
	}, [location.pathname]);

	const isAppPage = location.pathname !== '/' && 
		location.pathname !== '/courses' && 
		location.pathname !== '/media-hub' && 
		location.pathname !== '/users' && 
		location.pathname !== '/profile' && 
		location.pathname !== '/broadcasts' && 
		location.pathname !== '/quizzes' && 
		location.pathname !== '/question-bank' && 
		location.pathname !== '/flashcards' && 
		location.pathname !== '/tickets' && 
		location.pathname !== '/mobile-banner' && 
		location.pathname !== '/logs' && 
		location.pathname !== '/enterprise' && 
		!location.pathname.startsWith('/accounts') && 
		location.pathname !== '/finance';

	useEffect(() => {
		if (isAppPage) {
			setIsCollapsed(true);
		} else {
			setIsCollapsed(false);
		}
	}, [isAppPage]);

	useEffect(() => {
		const fetchRole = async () => {
			const loginId = user?.signInDetails?.loginId || user?.username;
			if (loginId) {
				try {
					const data = await apiGetRequest<undefined, { role?: string; status?: string }>(`/users/${loginId}`);
					if (data?.status?.toLowerCase() === 'inactive') {
						setIsSuspended(true);
						return;
					}
					setUserRole(data?.role || 'Instructor');
				} catch (error) {
					console.error("Failed to fetch user role, defaulting to Instructor", error);
					setUserRole('Instructor');
				}
			} else {
				setUserRole('Instructor');
			}
		};
		fetchRole();
	}, [user, signOut]);

	const NavItem = ({ to, icon, label, exact = false }: { to: string, icon: any, label: string, exact?: boolean }) => {
		let isActive = false;
		if (to.includes('?')) {
			const [path, search] = to.split('?');
			isActive = location.pathname === path && location.search.includes(search);
		} else if (exact || to === "/") {
			isActive = location.pathname === to && (!location.search || location.search === '');
		} else {
			isActive = location.pathname === to || location.pathname.startsWith(to + '/');
		}
		
		const activeBg = useColorModeValue("linear-gradient(90deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)", "linear-gradient(90deg, rgba(14, 165, 233, 0.2) 0%, rgba(16, 185, 129, 0.12) 100%)");
		const activeColor = useColorModeValue("#0284c7", "#38bdf8");
		const inactiveColor = useColorModeValue("gray.600", "gray.400");
		const hoverBgVal = useColorModeValue("gray.100", "rgba(255, 255, 255, 0.04)");
		
		return (
			<Tooltip label={isCollapsed ? label : ""} placement="right" hasArrow borderRadius="md" px={2.5} py={1} fontSize="xs">
				<Button
					justifyContent={isCollapsed ? "center" : "flex-start"}
					variant="ghost"
					bg={isActive ? activeBg : "transparent"}
					color={isActive ? activeColor : inactiveColor}
					fontWeight={isActive ? "600" : "500"}
					fontSize="13.5px"
					letterSpacing="-0.1px"
					leftIcon={<FontAwesomeIcon icon={icon} style={{ width: '16px', height: '16px', opacity: isActive ? 1 : 0.75, transition: 'all 0.2s' }} />}
					onClick={() => navigate(to)}
					_hover={{ 
						bg: isActive ? activeBg : hoverBgVal, 
						color: isActive ? activeColor : useColorModeValue("gray.900", "white"),
					}}
					transition="all 0.18s ease"
					px={isCollapsed ? 0 : 3.5}
					py={2.5}
					h="38px"
					borderRadius="10px"
					w="100%"
					position="relative"
				>
					{isActive && !isCollapsed && (
						<Box 
							position="absolute" 
							left="2px" 
							top="20%" 
							h="60%" 
							w="3.5px" 
							borderRadius="full" 
							bgGradient="linear(to-b, #0ea5e9, #10b981)" 
						/>
					)}
					{!isCollapsed && <Text ml={1.5} noOfLines={1}>{label}</Text>}
				</Button>
			</Tooltip>
		);
	};

	const SubNavItem = ({ to, icon, label, isActive }: { to: string, icon: any, label: string, isActive: boolean }) => {
		const activeBg = useColorModeValue("linear-gradient(90deg, rgba(14, 165, 233, 0.1) 0%, rgba(16, 185, 129, 0.06) 100%)", "linear-gradient(90deg, rgba(14, 165, 233, 0.16) 0%, rgba(16, 185, 129, 0.1) 100%)");
		const activeColor = useColorModeValue("#0284c7", "#38bdf8");
		const inactiveColor = useColorModeValue("gray.600", "gray.400");
		const hoverBgVal = useColorModeValue("gray.100", "rgba(255, 255, 255, 0.04)");

		return (
			<Button
				justifyContent="flex-start"
				variant="ghost"
				bg={isActive ? activeBg : "transparent"}
				color={isActive ? activeColor : inactiveColor}
				fontWeight={isActive ? "700" : "500"}
				fontSize="12px"
				leftIcon={<FontAwesomeIcon icon={icon} style={{ width: '13px', height: '13px', opacity: isActive ? 1 : 0.7 }} />}
				onClick={() => navigate(to)}
				_hover={{ 
					bg: isActive ? activeBg : hoverBgVal, 
					color: isActive ? activeColor : useColorModeValue("gray.900", "white"),
				}}
				transition="all 0.15s ease"
				pl={6}
				pr={3}
				py={1.5}
				h="32px"
				borderRadius="8px"
				w="100%"
				position="relative"
			>
				{isActive && (
					<Box 
						position="absolute" 
						left="12px" 
						top="30%" 
						h="40%" 
						w="3px" 
						borderRadius="full" 
						bgGradient="linear(to-b, #0ea5e9, #10b981)" 
					/>
				)}
				<Text ml={1} noOfLines={1}>{label}</Text>
			</Button>
		);
	};

	if (isSuspended) {
		return (
			<Box
				position="fixed"
				top={0}
				left={0}
				right={0}
				bottom={0}
				bg="rgba(10, 10, 15, 0.95)"
				backdropFilter="blur(20px)"
				zIndex={9999}
				display="flex"
				alignItems="center"
				justifyContent="center"
				p={6}
			>
				<Box
					bg="gray.900"
					border="1px solid"
					borderColor="red.500/30"
					borderRadius="3xl"
					p={8}
					maxW="480px"
					textAlign="center"
					boxShadow="0 25px 50px -12px rgba(239, 68, 68, 0.25)"
				>
					<Text fontSize="2xl" fontWeight="black" color="red.400" mb={3}>
						Account Suspended
					</Text>
					<Text fontSize="lg" color="gray.300" mb={8} lineHeight="tall">
						Your account has been suspended. Please contact the administrative or XNav team to resolve this issue and restore your access.
					</Text>
					<Button
						size="lg"
						colorScheme="red"
						variant="outline"
						onClick={() => signOut()}
						_hover={{ bg: "red.600", color: "white", borderColor: "red.600" }}
						transition="all 0.3s"
					>
						Return to Login
					</Button>
				</Box>
			</Box>
		);
	}

	const isSuperAdmin = userRole === 'Super Admin';
	const isAdmin = userRole === 'Admin';
	const isOrgAdmin = userRole === 'Org Admin';
	const canSeeUserManagement = isSuperAdmin || isAdmin || isOrgAdmin;
	const canSeeFullAdmin = isSuperAdmin || isAdmin;
	const canSeeAnalytics = true;

	const searchParam = new URLSearchParams(location.search);
	const currentTab = searchParam.get('tab') || 'overview';
	const isOverviewActive = location.pathname === '/broadcasts' && (currentTab === 'overview' || !location.search);
	const isNetworkAlertActive = location.pathname === '/broadcasts' && currentTab === 'network-alert';
	const isWebEmailActive = location.pathname === '/broadcasts' && currentTab === 'web-email';
	const isMobileEmailActive = location.pathname === '/broadcasts' && currentTab === 'mobile-email';
	const isMobileBannerActive = location.pathname === '/mobile-banner';

	const accountsTab = searchParam.get('tab') || 'dashboard';
	const isAccountsDashboardActive = isAccountsRoute && (location.pathname === '/accounts/dashboard' || (location.pathname === '/accounts' && (accountsTab === 'dashboard' || !searchParam.get('tab'))));
	const isAccountsAndroidActive = isAccountsRoute && (location.pathname === '/accounts/android' || (isAccountsRoute && accountsTab === 'android'));
	const isAccountsIosActive = isAccountsRoute && (location.pathname === '/accounts/ios' || (isAccountsRoute && accountsTab === 'ios'));
	const isAccountsReceiptsActive = isAccountsRoute && (location.pathname === '/accounts/receipts' || (isAccountsRoute && accountsTab === 'receipts'));

	return (
		<Flex
			w={isCollapsed ? "72px" : "240px"}
			transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
			h="100vh"
			bg={bg}
			backdropFilter="blur(20px)"
			borderRight="1px solid"
			borderColor={borderColor}
			flexDir="column"
			py={5}
			px={isCollapsed ? 2 : 3}
			position="sticky"
			top={0}
			zIndex={100}
			boxShadow={useColorModeValue("4px 0 20px rgba(0, 0, 0, 0.01)", "4px 0 20px rgba(0, 0, 0, 0.15)")}
		>
			<Flex 
				alignItems="center" 
				mb={4} 
				px={isCollapsed ? 0 : 2} 
				justifyContent={isCollapsed ? "center" : "flex-start"} 
				cursor="pointer" 
				onClick={() => setIsCollapsed(!isCollapsed)}
				_hover={{ opacity: 0.9 }}
				transition="all 0.2s"
			>
				{!isCollapsed ? (
					<HStack spacing={2.5} align="center">
						<Image src={logoSymbol} alt="XNAV Logo" w="30px" h="30px" objectFit="contain" filter={useColorModeValue("drop-shadow(0 2px 8px rgba(14, 165, 233, 0.3))", "drop-shadow(0 2px 10px rgba(56, 189, 248, 0.5))")} />
						<Box>
							<Text 
								fontSize="17px" 
								fontWeight="500" 
								color={useColorModeValue("gray.800", "whiteAlpha.900")} 
								letterSpacing="1px" 
								lineHeight="1.1"
								display="inline-flex"
								alignItems="center"
							>
								XN
								<Text
									as="span"
									aria-label="A"
									fontWeight="600"
									fontSize="18px"
									bgGradient={useColorModeValue("linear(to-br, #0284c7, #0ea5e9, #10b981)", "linear(to-br, #38bdf8, #0ea5e9, #34d399)")}
									bgClip="text"
									display="inline-block"
									filter={useColorModeValue(
										"drop-shadow(0 0 8px rgba(14, 165, 233, 0.5))",
										"drop-shadow(0 0 12px rgba(56, 189, 248, 0.75)) drop-shadow(0 0 20px rgba(16, 185, 129, 0.4))"
									)}
									transform="scale(1.04)"
									mx="0.5px"
								>
									Λ
								</Text>
								V
								<Text 
									as="span" 
									color={useColorModeValue("#0284c7", "#38bdf8")} 
									fontSize="10px" 
									fontWeight="600" 
									textTransform="uppercase" 
									letterSpacing="0.8px" 
									ml={2} 
									px={1.5} 
									py={0.5} 
									bg={useColorModeValue("linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)", "linear-gradient(135deg, rgba(14, 165, 233, 0.18) 0%, rgba(16, 185, 129, 0.18) 100%)")} 
									borderRadius="md" 
									border="1px solid" 
									borderColor={useColorModeValue("rgba(14, 165, 233, 0.25)", "rgba(56, 189, 248, 0.35)")}
								>
									Studio
								</Text>
							</Text>
						</Box>
					</HStack>
				) : (
					<Image src={logoSymbol} alt="XNAV Logo" w="32px" h="32px" objectFit="contain" filter={useColorModeValue("drop-shadow(0 2px 8px rgba(14, 165, 233, 0.35))", "drop-shadow(0 2px 10px rgba(56, 189, 248, 0.6))")} />
				)}
			</Flex>

			<VStack align="stretch" spacing={1} flex={1} mt={1} overflowY="auto" overflowX="hidden">
				{!isCollapsed && (
					<Text fontSize="10px" fontWeight="700" color={useColorModeValue("gray.400", "gray.500")} letterSpacing="1px" textTransform="uppercase" px={3} mb={0.5} mt={1}>
						Main
					</Text>
				)}
				{canSeeAnalytics && (
					<NavItem to="/" icon={faHome} label="Dashboard" exact={true} />
				)}
				<NavItem to="/courses" icon={faFolderOpen} label="My Courses" />
				<NavItem to="/analytics" icon={faChartLine} label="Student Analytics" />

				<Divider my={2} borderColor={useColorModeValue("gray.200", "rgba(255,255,255,0.08)")} />

				{!isCollapsed && (
					<Text fontSize="10px" fontWeight="700" color={useColorModeValue("gray.400", "gray.500")} letterSpacing="1px" textTransform="uppercase" px={3} mb={0.5} mt={0.5}>
						Studio Engines
					</Text>
				)}
				<NavItem to="/media-hub" icon={faPhotoFilm} label="Asset Hub" />
				<NavItem to="/quizzes" icon={faFileAlt} label="Quiz Engine" />
				<NavItem to="/question-bank" icon={faQuestionCircle} label="Question Bank" />
				<NavItem to="/flashcards" icon={faBullhorn} label="Flashcard Engine" />

				{(canSeeUserManagement || canSeeFullAdmin) && (
					<>
						<Divider my={2} borderColor={useColorModeValue("gray.200", "rgba(255,255,255,0.08)")} />
						{!isCollapsed && (
							<Text fontSize="10px" fontWeight="700" color={useColorModeValue("gray.400", "gray.500")} letterSpacing="1px" textTransform="uppercase" px={3} mb={0.5} mt={0.5}>
								Administration
							</Text>
						)}
						{canSeeUserManagement && (
							<NavItem to="/users" icon={faCog} label="User Management" />
						)}
						
						{canSeeFullAdmin && (
							<>
								{/* Accounts & Finance Dropdown */}
								<Box>
									{isCollapsed ? (
										<NavItem to="/accounts?tab=dashboard" icon={faFileInvoiceDollar} label="Accounts & Finance" />
									) : (
										<>
											<Button
												justifyContent="space-between"
												variant="ghost"
												bg={isAccountsRoute ? useColorModeValue("linear-gradient(90deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)", "linear-gradient(90deg, rgba(14, 165, 233, 0.18) 0%, rgba(16, 185, 129, 0.12) 100%)") : "transparent"}
												color={isAccountsRoute ? useColorModeValue("#0284c7", "#38bdf8") : useColorModeValue("gray.600", "gray.400")}
												fontWeight={isAccountsRoute ? "600" : "500"}
												fontSize="13.5px"
												letterSpacing="-0.1px"
												onClick={() => {
													if (!isAccountsRoute) {
														navigate('/accounts?tab=dashboard');
													}
													setIsAccountsExpanded(!isAccountsExpanded);
												}}
												_hover={{ 
													bg: useColorModeValue("linear-gradient(90deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)", "linear-gradient(90deg, rgba(14, 165, 233, 0.18) 0%, rgba(16, 185, 129, 0.12) 100%)"),
													color: useColorModeValue("#0284c7", "#38bdf8") 
												}}
												px={3.5}
												py={2.5}
												h="38px"
												borderRadius="10px"
												w="100%"
											>
												<Flex align="center">
													<FontAwesomeIcon icon={faFileInvoiceDollar} style={{ width: '16px', height: '16px', opacity: isAccountsRoute ? 1 : 0.75 }} />
													<Text ml={3} noOfLines={1}>Accounts & Finance</Text>
												</Flex>
												<FontAwesomeIcon icon={isAccountsExpanded ? faChevronDown : faChevronRight} style={{ fontSize: '10px', opacity: 0.6 }} />
											</Button>

											<Collapse in={isAccountsExpanded} animateOpacity>
												<VStack align="stretch" spacing={0.5} mt={1} mb={1}>
													<SubNavItem to="/accounts?tab=dashboard" icon={faChartLine} label="Dashboard" isActive={isAccountsDashboardActive} />
													<SubNavItem to="/accounts?tab=android" icon={faMobileScreen} label="Android" isActive={isAccountsAndroidActive} />
													<SubNavItem to="/accounts?tab=ios" icon={faStore} label="iOS" isActive={isAccountsIosActive} />
													<SubNavItem to="/accounts?tab=receipts" icon={faReceipt} label="Receipts & Subscriptions" isActive={isAccountsReceiptsActive} />
												</VStack>
											</Collapse>
										</>
									)}
								</Box>

								<NavItem to="/tickets" icon={faLifeRing} label="Mobile Support Tickets" />
								
								<Box>
									{isCollapsed ? (
										<NavItem to="/broadcasts" icon={faTowerBroadcast} label="Broadcasts" />
									) : (
										<>
											<Button
												justifyContent="space-between"
												variant="ghost"
												bg={isBroadcastRoute ? useColorModeValue("linear-gradient(90deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)", "linear-gradient(90deg, rgba(14, 165, 233, 0.18) 0%, rgba(16, 185, 129, 0.12) 100%)") : "transparent"}
												color={isBroadcastRoute ? useColorModeValue("#0284c7", "#38bdf8") : useColorModeValue("gray.600", "gray.400")}
												fontWeight={isBroadcastRoute ? "600" : "500"}
												fontSize="13.5px"
												letterSpacing="-0.1px"
												onClick={() => {
													if (!isBroadcastRoute) {
														navigate('/broadcasts');
													}
													setIsBroadcastExpanded(!isBroadcastExpanded);
												}}
												_hover={{ 
													bg: useColorModeValue("linear-gradient(90deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)", "linear-gradient(90deg, rgba(14, 165, 233, 0.18) 0%, rgba(16, 185, 129, 0.12) 100%)"),
													color: useColorModeValue("#0284c7", "#38bdf8") 
												}}
												px={3.5}
												py={2.5}
												h="38px"
												borderRadius="10px"
												w="100%"
											>
												<Flex align="center">
													<FontAwesomeIcon icon={faTowerBroadcast} style={{ width: '16px', height: '16px', opacity: isBroadcastRoute ? 1 : 0.75 }} />
													<Text ml={3} noOfLines={1}>Broadcasts</Text>
												</Flex>
												<FontAwesomeIcon icon={isBroadcastExpanded ? faChevronDown : faChevronRight} style={{ fontSize: '10px', opacity: 0.6 }} />
											</Button>

											<Collapse in={isBroadcastExpanded} animateOpacity>
												<VStack align="stretch" spacing={0.5} mt={1} mb={1}>
													<SubNavItem to="/broadcasts?tab=overview" icon={faChartPie} label="Dashboard" isActive={isOverviewActive} />
													<SubNavItem to="/broadcasts?tab=network-alert" icon={faBullhorn} label="Network Alert" isActive={isNetworkAlertActive} />
													<SubNavItem to="/broadcasts?tab=web-email" icon={faEnvelope} label="Web Email" isActive={isWebEmailActive} />
													<SubNavItem to="/broadcasts?tab=mobile-email" icon={faMobileScreen} label="Mobile Email" isActive={isMobileEmailActive} />
													<SubNavItem to="/mobile-banner" icon={faPhotoFilm} label="Mobile Banner" isActive={isMobileBannerActive} />
												</VStack>
											</Collapse>
										</>
									)}
								</Box>

								<NavItem to="/logs" icon={faListAlt} label="Global Logs" />
								<NavItem to="/enterprise" icon={faCube} label="Enterprise" />
							</>
						)}
					</>
				)}
			</VStack>
		</Flex>
	);
}
